import React, { useEffect, useMemo, useRef, useState } from 'react'

import { IncomeCalcLocation } from '../../models/IncomeCalcLocation.model'
import { checkIsUpTree, scrollIntoView } from '../../utils'
import { findExactCityMatch } from '../../utils/calculatorCity'

type CalculatorCityAutocompleteProps = {
  cityQuery: string
  locations: IncomeCalcLocation[]
  totalLocationCount?: number
  disabled: boolean
  loading: boolean
  isCityValid?: boolean
  helperText?: string
  validationError?: string
  onCityQueryChange: (value: string) => void
  onCitySelect: (city: string) => void
  onCityBlur?: () => void
}

const highlightMatch = (text: string, query: string) => {
  const trimmedQuery = query.trim()
  if (!trimmedQuery) {
    return text
  }

  const lowerText = text.toLowerCase()
  const lowerQuery = trimmedQuery.toLowerCase()
  const matchIndex = lowerText.indexOf(lowerQuery)

  if (matchIndex === -1) {
    return text
  }

  return (
    <>
      {text.slice(0, matchIndex)}
      <mark className="rounded bg-orange/20 px-0.5 text-graphite-grey">
        {text.slice(matchIndex, matchIndex + trimmedQuery.length)}
      </mark>
      {text.slice(matchIndex + trimmedQuery.length)}
    </>
  )
}

export const CalculatorCityAutocomplete: React.FC<CalculatorCityAutocompleteProps> = ({
  cityQuery,
  locations,
  totalLocationCount,
  disabled,
  loading,
  isCityValid = false,
  helperText,
  validationError,
  onCityQueryChange,
  onCitySelect,
  onCityBlur,
}) => {
  const [termFocused, setTermFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const skipBlurValidationRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const autoCompleteContainerRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([])

  const showDropdown = termFocused && !disabled && (loading || cityQuery.length > 0 || locations.length > 0)

  const listHint = useMemo(() => {
    if (loading || validationError || helperText) {
      return null
    }

    const total = totalLocationCount ?? locations.length
    const trimmedQuery = cityQuery.trim()

    if (total === 0) {
      return null
    }

    if (!trimmedQuery) {
      return `${total} ${total === 1 ? 'city' : 'cities'} available — type to filter`
    }

    if (locations.length === total) {
      return `${locations.length} ${locations.length === 1 ? 'city' : 'cities'} match`
    }

    return `Showing ${locations.length} of ${total} cities`
  }, [cityQuery, helperText, loading, locations.length, totalLocationCount, validationError])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [cityQuery, locations])

  useEffect(() => {
    if (!showDropdown) {
      return
    }

    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, showDropdown])

  const selectCity = (city: string) => {
    skipBlurValidationRef.current = true
    onCitySelect(city)
    setTermFocused(false)
    inputRef.current?.blur()
  }

  const selectCityFromQuery = () => {
    const exactMatch = findExactCityMatch(cityQuery, locations)
    if (exactMatch) {
      selectCity(exactMatch)
      return true
    }

    if (locations.length === 1) {
      selectCity(locations[0].city)
      return true
    }

    return selectHighlightedCity()
  }

  const selectHighlightedCity = () => {
    const location = locations[highlightedIndex]
    if (location) {
      selectCity(location.city)
      return true
    }
    return false
  }

  const inputBorderClass = validationError
    ? 'border-red-300 focus:ring-red-400'
    : 'border-gray-300 focus:ring-royal-indigo'

  return (
    <div className="relative">
      <label
        className="mb-1.5 block text-xs font-semibold text-graphite-grey sm:text-sm"
        htmlFor="calculator-city-input"
      >
        City <span className="text-red-500">*</span>
      </label>

      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
        </span>

        <input
          ref={inputRef}
          id="calculator-city-input"
          name="city"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls="calculator-city-listbox"
          aria-activedescendant={
            showDropdown && locations[highlightedIndex] ? `calculator-city-option-${highlightedIndex}` : undefined
          }
          className={`w-full rounded-lg border bg-white py-2.5 pl-9 pr-10 text-sm focus:border-transparent focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-100 sm:pl-10 sm:pr-10 ${inputBorderClass}`}
          placeholder={disabled ? 'Select state and date first' : 'Search city'}
          value={cityQuery}
          disabled={disabled}
          autoComplete="off"
          onChange={(evt) => {
            onCityQueryChange(evt.target.value)
            setTermFocused(true)
          }}
          onFocus={(evt) => {
            scrollIntoView(evt.target)
            if (!disabled) {
              setTermFocused(true)
            }
          }}
          onBlur={(evt) => {
            if (!checkIsUpTree(evt.relatedTarget, autoCompleteContainerRef.current)) {
              setTermFocused(false)
              if (skipBlurValidationRef.current) {
                skipBlurValidationRef.current = false
                return
              }
              onCityBlur?.()
            }
          }}
          onKeyDown={(evt) => {
            if (evt.key === 'ArrowDown') {
              evt.preventDefault()
              if (locations.length > 0) {
                setTermFocused(true)
                setHighlightedIndex((prev) => Math.min(prev + 1, locations.length - 1))
              }
              return
            }

            if (evt.key === 'ArrowUp') {
              evt.preventDefault()
              if (locations.length > 0) {
                setTermFocused(true)
                setHighlightedIndex((prev) => Math.max(prev - 1, 0))
              }
              return
            }

            if (evt.key === 'Enter') {
              evt.preventDefault()
              if (selectCityFromQuery()) {
                return
              }
              onCityBlur?.()
              return
            }

            if (evt.key === 'Escape') {
              evt.preventDefault()
              setTermFocused(false)
              inputRef.current?.blur()
            }
          }}
        />

        {cityQuery && !disabled ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Clear city"
            onMouseDown={(evt) => evt.preventDefault()}
            onClick={() => {
              onCityQueryChange('')
              setHighlightedIndex(0)
              inputRef.current?.focus()
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        ) : null}
      </div>

      {listHint && !validationError && <p className="mt-1 text-xs text-gray-500">{listHint}</p>}
      {helperText && !validationError && !listHint && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}
      {validationError && (
        <div className="validation-error mt-1 text-sm text-red-500" tabIndex={-1}>
          {validationError}
        </div>
      )}

      {showDropdown && (
        <div
          id="calculator-city-listbox"
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          ref={autoCompleteContainerRef}
        >
          {loading ? (
            <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-royal-indigo" />
              Loading cities...
            </div>
          ) : locations.length > 0 ? (
            locations.map((location, index) => {
              const isHighlighted = index === highlightedIndex
              const isSelected = isCityValid && cityQuery.trim().toLowerCase() === location.city.toLowerCase()

              return (
                <button
                  key={location.city}
                  id={`calculator-city-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={isHighlighted || isSelected}
                  ref={(element) => {
                    optionRefs.current[index] = element
                  }}
                  className={`flex w-full flex-col border-b border-gray-100 px-3 py-2.5 text-left last:border-0 ${
                    isHighlighted ? 'bg-royal-indigo/10' : 'hover:bg-gray-50'
                  }`}
                  onMouseDown={(evt) => evt.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => selectCity(location.city)}
                >
                  <span className="text-sm font-medium text-graphite-grey">
                    {highlightMatch(location.city, cityQuery)}
                  </span>
                  {location.county ? <span className="text-xs text-gray-500">{location.county}</span> : null}
                  {location.standardRate ? <span className="text-xs text-gray-500">Standard rate area</span> : null}
                </button>
              )
            })
          ) : (
            <div className="p-3 text-sm text-gray-500">No matching cities — check spelling or try another name</div>
          )}
        </div>
      )}
    </div>
  )
}
